import { store } from "@app/store";
import { Button } from "@components/button";
import { Modal } from "@components/modal";
import { useAppeals } from "@features/appeals";
import {
  PaymentCredited,
  PaymentNotCredited,
} from "@features/appeals/components/status/index";
import { removeFile, resetLastAppeal } from "@features/appeals/slice";
import { useAuth } from "@features/auth";
import {
  MerchantOrder,
  MerchantOrderStatus,
  // MerchantOrderStatus,
  // MerchantOrderType,
} from "@features/merchantOrder/api";
import { formatDate, formatTime } from "@helpers/date";
import React, { useEffect, useState } from "react";
import styles from "./modal.module.scss";
import { useNotification } from "@hooks/use-notification";
import { useForm } from "react-hook-form";
import { Input } from "@components/input";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatRubles, formatUsdt } from "@helpers/format";
import { useMerchantOrders } from "@features/merchantOrder";
import { EllipsisText } from "@components/ellipsis-text";
import { CreateAppealRequest } from "@features/appeals/api";
import { Config, DoubleText } from "@components/table";
import { RowTable } from "@components/row-table";
import { StatusBlock, StatusBlockType } from "@components/status-block";

type Props = {
  isModalOpen: boolean;
  hideModal: () => void;
  data: MerchantOrder;
};

type ImageLinkByDate = {
  date: string;
  links: string[];
};

type ForForm = {
  actualReceivedAmount?: number;
};

const schema: yup.ObjectSchema<ForForm> = yup.object().shape({
  actualReceivedAmount: yup.number().transform((v, o) => o === '' ? undefined : v),
});

export const DealModal = ({ hideModal, isModalOpen, data }: Props) => {
  // const [sent, setSent] = useState<boolean>(false);

  const notif = useNotification();
  const {
    state,
    getAppealsByOrderId,
    uploadReceipt,
    createAppeal,
    getMerchantAppeals,
  } = useAppeals();
  const { state: authState } = useAuth();
  const [imagesByDate, setImagesByDate] = useState<ImageLinkByDate[]>([]);
  const [message, setMessage] = useState("");
  const [disabled, setDisabled] = useState(true);
  const { control, handleSubmit, setValue } = useForm<ForForm>({
    resolver: yupResolver(schema),
  });
  const { getMerchantOrders } = useMerchantOrders();

  useEffect(() => {
    store.dispatch(resetLastAppeal());
    setImagesByDate([]);
    getAppealsByOrderId(data.invid);
  }, [getAppealsByOrderId, data.invid]);

  useEffect(() => {
    if (state.appealsByOrderId == null) return;
    const dateToLinksMap = new Map<string, Set<string>>();
    // Process each appeal using the full created_at timestamp
    for (const appeal of state.appealsByOrderId.appeals) {
      const date = appeal.created_at;
      if (!dateToLinksMap.has(date)) {
        dateToLinksMap.set(date, new Set<string>());
      }
      const linkSet = dateToLinksMap.get(date)!;
      if (
        appeal.check_image_links == null ||
        appeal.check_image_links.length === 0
      ) {
        linkSet.add("Нет файлов");
      } else {
        for (const link of appeal.check_image_links) {
          linkSet.add(link); // Ensure uniqueness of links for this timestamp
        }
      }
    }
    // Convert Map to array of ImageLinkByDate objects
    const result: ImageLinkByDate[] = [];
    for (const [date, linkSet] of dateToLinksMap) {
      result.push({ date, links: Array.from(linkSet) });
    }
    // Sort by full timestamp
    result.sort((a, b) => a.date.localeCompare(b.date));
    setImagesByDate(result);
  }, [state.appealsByOrderId, state.lastAppeal?.check_image_links]);

  useEffect(() => {
    if (state.lastAppeal) {
      setMessage(state.lastAppeal.message);
    } else {
      setMessage("");
    }
  }, [state.lastAppeal, isModalOpen]);

  useEffect(() => {
    setDisabled(message.length == 0 || state.receiptUrls.length == 0);
  }, [message, state.receiptUrls]);

  const closeModal = () => {
    hideModal();
    setMessage("");
    setValue("actualReceivedAmount", undefined);
  };

  const sendDealRequestData = async (formData: ForForm) => {
    if (authState.email == null) return;
    if (message.length == 0) {
      notif.addNotification("Сообщение не должно быть пустым");
      return;
    }
    if (state.receiptUrls.length == 0) {
      notif.addNotification("Должны быть прикреплены фотографии чеков");
      return;
    }
    const request: CreateAppealRequest = {
      images: state.receiptUrls,
      order: data.id,
      message: message,
      merchant_amount: formData.actualReceivedAmount,
      order_type: "pay_in",
    };
    createAppeal(request).then(() => {
      closeModal();
      // notif.addNotification("Создано!");
      getMerchantAppeals({ limit: 10, page: 1, status: "processing" });
      getMerchantOrders({ limit: 100, page: 1 });
    });
    // setSent(true);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files == null || data.invid == null) return;
    const file = event.target.files;
    uploadReceipt(data.invid, file).then(() => {
      notif.addNotification("Файл добавлен");
    });
  };

  const config: Config<MerchantOrder> = [
    {
      label: "Номер заявки",
      source: "id",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    {
      label: "Invid заявки",
      source: "invid",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    { label: "Касса", source: "cache" },
    {
      label: "Дата",
      source: "invid",
      renderComponent: () =>
        !state || state.loadingByOrder ? (
          "Loading"
        ) : (
          <DoubleText
            text={formatTime(
              state.lastAppeal
                ? new Date(state.lastAppeal.created_at)
                : new Date()
            )}
            subText={formatDate(
              state.lastAppeal
                ? new Date(state.lastAppeal.created_at)
                : new Date()
            )}
          />
        ),
    },
    {
      label: "Статус",
      source: "appeal",
      renderComponent: () => {
        let text = "";
        let type = StatusBlockType.Success;

        switch (data.status) {
          case MerchantOrderStatus.Canceled:
            text = "Отменен";
            type = StatusBlockType.Error;
            break;
          case MerchantOrderStatus.Completed:
            text = "Зачислен";
            type = StatusBlockType.Success;
            break;
          case MerchantOrderStatus.Pending:
            text = "В процессе";
            type = StatusBlockType.Warning;
            break;
          case MerchantOrderStatus.DisputeOpen:
            text = "Спор открыт";
            type = StatusBlockType.Warning;
            break;
        }

        return (
          <StatusBlock type={type} text={text} style={{ textWrap: "nowrap" }} />
        );
      },
    },
    {
      label: "Способ",
      source: "method"
    },
    {
      label: "Банк",
      source: "bank"
    },
    {
      label: "Реквизит",
      source: "requisite"
    },
    {
      label: "ID Trader",
      source: "exchange_rate",
      renderComponent: () => (
        <EllipsisText
          text={state.appealsByOrderId?.trader_id ?? ""}
          copy
          startLength={10}
        />
      ),
    },
    {
      label: "Сумма заявки",
      source: "order_start_amount_usdt",
      renderComponent: (data, item) => (
        <DoubleText
          text={formatRubles(item.order_start_amount)}
          subText={formatUsdt(data)}
        />
      ),
    },
    {
      label: "Итоговая сумма",
      source: "order_final_amount_usdt",
      renderComponent: (data, item) => (
        <DoubleText
          text={formatRubles(item.order_final_amount)}
          subText={formatUsdt(data)}
        />
      ),
    },
    {
      label: "Измененная сумма",
      source: "order_final_amount_usdt",
      renderComponent: () => {
        return !isSendVisible ? (
          state.lastAppeal != null &&
            state.lastAppeal.merchant_amount != null && (
              <DoubleText
                text={formatRubles(state.lastAppeal.merchant_amount)}
                subText={formatUsdt(state.lastAppeal.merchant_amount_usdt ?? 0)}
              />
            )
        ) : (
          <Input
            control={control}
            name="actualReceivedAmount"
            // disabled={state.lastAppeal != null}
            type="number"
          />
        );
      },
    },
    {
      label: "Курс",
      source: "exchange_rate",
      renderComponent: (data) => formatUsdt(data, true),
    },
    {
      label: "Файлы",
      source: "exchange_rate",
      renderComponent: () => {
        return state.loadingByOrder || state.isReceiptLoading ? (
          "Loading"
        ) : (
          <div className={styles.linksWrapper}>
            {imagesByDate.length > 0 &&
              imagesByDate.map((obj) => (
                <div key={obj.date} className={styles.imageLinks}>
                  <span className="font-semibold text-[14px] mb-[5px] mt-[5px]">
                    {formatDate(new Date(obj.date)) +
                      " " +
                      formatTime(new Date(obj.date))}
                  </span>
                  {obj.links.map((file) => (
                    <React.Fragment key={file}>
                      <a className="underline" href={file} target="_blank">
                        {file.split("/").pop()}
                      </a>
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              ))}
            <>
              {state.receiptUrls.map((file) => (
                <div
                  key={file}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                >
                  <a className="underline" href={file} target="_blank">
                    {file.split("/").pop()}
                  </a>
                  <span
                    style={{ color: "red" }}
                    onClick={() => {
                      store.dispatch(removeFile(file));
                    }}
                  >
                    ×
                  </span>
                </div>
              ))}
              {isSendVisible && (
                <label style={{ cursor: "pointer" }}>
                  Добавить
                  <input
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </label>
              )}
            </>
          </div>
        );
      },
    },
    {
      label: "Ваше сообщение",
      source: "exchange_rate",
      renderComponent: () => (
        <textarea
          disabled={!isSendVisible}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        ></textarea>
      ),
    },
  ];

  const isSendVisible =
    !state.loadingByOrder &&
    (data.status == "completed" || data.status == "canceled");
  // state.lastAppeal?.status !== "processing" && // статус последней аппеляции не на рассмотрении
  // (state.appealsByOrderId?.appeals.length ?? 0) < 3 && // не больше 3-х аппеляций
  // state.lastAppeal?.status != "approved") ||

  return (
    <Modal
      className={styles.modal}
      isOpen={isModalOpen}
      onClose={closeModal}
      title={
        state.loadingByOrder
          ? "Loading"
          : state.lastAppeal
            ? "Аппеляция №" + state.lastAppeal.id
            : "Новая аппеляция"
      }
    >
      {state.lastAppeal?.status === "approved" && !state.loadingByOrder && (
        <PaymentCredited text="Аппеляция принята" />
      )}

      {state.lastAppeal?.status === "canceled" && !state.loadingByOrder && (
        <PaymentNotCredited text="Аппеляция отменена" />
      )}
      {/* {state.lastAppeal?.status === MerchantOrderStatus.Pending &&
        !state.loadingByOrder && <PaymentProcessing />} */}

      <RowTable config={config} data={data} />

      {isSendVisible && (
        <Button disabled={disabled} onClick={handleSubmit(sendDealRequestData)}>
          Отправить
        </Button>
      )}
    </Modal>
  );
};
